import React from "react";
import { ExternalLink, Wrench, Database as DatabaseIcon } from "lucide-react";
import Footer from "../components/Footer";

const OtherResources = () => {
  const tools = [
    {
      category: "Tools to predict OriC",
      items: [
        {
          name: "Ori-Finder 2022",
          url: "https://tubic.org/Ori-Finder2022/public/index.php",
          description:
            "A web server to predict bacterial oriCs by integrating the Z-curve method, DnaA box distribution, and comparative genomic analysis",
        },
        {
          name: "OriV-Finder",
          url: "https://tubic.org/OriV-Finder/",
          description:
            "A comprehensive web server for bacterial plasmid replication origin analysis",
        },
        {
          name: "ORCA",
          url: "https://github.com/ZoyavanMeel/ORCA",
          description:
            "A command line tool which uses the analysis of nucleotide disparities, dnaA-box regions, and target gene positions to find potential origin sites, and has a random forest classifier to predict origin sites",
        },
        {
          name: "ORI-Explorer",
          url: "https://github.com/Z-Abbas/ORI-Explorer",
          description:
            "A unified cell-specific tool for Origin of Replication sites Prediction by Feature Fusion",
        },
      ],
    },
    {
      category: "Tools to circularize the bacterial genome",
      items: [
        {
          name: "Circlator",
          url: "https://github.com/sanger-pathogens/circlator",
          description:
            "It attempts to identify each circular sequence and output a linearised version of it. It does this by assembling all reads that map to contig ends and comparing the resulting contigs with the input assembly. Note: This tool is no longer maintained.",
          warning: true,
        },
      ],
    },
    {
      category: "Databases",
      items: [
        {
          name: "DoriC 12",
          url: "https://tubic.org/doric/",
          description:
            "The oriCs of hundreds of sequenced bacterial genomes have been annotated in the genome reports using Ori-Finder and the predicted results have been deposited in DoriC, a manually curated database of oriCs",
        },
      ],
    },
  ];

  return (
    <>
      <main className="page other-resources-page min-h-[80vh] py-16 px-6 md:px-16 bg-white">
        <section className="max-w-6xl mx-auto">
          <div className="mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Resources
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-full bg-emerald-100">
                <Wrench className="w-5 h-5 text-emerald-700" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
                Other <span className="text-emerald-700">Resources</span>
              </h1>
            </div>
            <p className="text-sm md:text-base leading-relaxed text-slate-700 max-w-3xl">
              Explore additional tools and databases that help researchers with
              decoding replication origins. These resources complement Origenomi
              and provide alternative methods and comprehensive datasets for
              bacterial genome analysis.
            </p>
          </div>

          {/* TOOLS AND DATABASES SECTIONS */}
          <div className="space-y-12">
            {tools.map((section, sectionIdx) => (
              <div key={sectionIdx}>
                {/* Section Header with Icon */}
                <div className="mb-6">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold mb-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    {section.category}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-slate-100">
                      {section.category === "Databases" ? (
                        <DatabaseIcon className="w-5 h-5 text-slate-700" />
                      ) : (
                        <Wrench className="w-5 h-5 text-slate-700" />
                      )}
                    </div>
                    <h2 className="text-2xl md:text-3xl font-semibold text-slate-900">
                      {section.category}
                    </h2>
                  </div>
                </div>

                {/* Resource Cards */}
                <div className="grid md:grid-cols-2 gap-6">
                  {section.items.map((item, itemIdx) => (
                    <div
                      key={itemIdx}
                      className={`bg-slate-50 border ${
                        item.warning ? "border-amber-300" : "border-slate-200"
                      } rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200`}
                    >
                      {/* Tool Name */}
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-lg font-semibold text-slate-900">
                          {item.name}
                        </h3>
                        <ExternalLink className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />
                      </div>

                      {/* Description */}
                      <p className="text-sm leading-relaxed text-slate-700 mb-4">
                        {item.description}
                      </p>

                      {/* Warning Badge (if applicable) */}
                      {item.warning && (
                        <div className="mb-4 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                          ⚠️ No longer maintained
                        </div>
                      )}

                      {/* Link Button */}
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200"
                      >
                        Visit Resource
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Additional Info Section */}
          <div className="mt-16 bg-gradient-to-br from-emerald-50 to-blue-50 border border-slate-200 rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-slate-900 mb-3">
              Want to suggest a resource?
            </h3>
            <p className="text-sm md:text-base leading-relaxed text-slate-700 mb-4">
              If you know of other valuable tools or databases for bacterial
              genome analysis and origin of replication prediction, we'd love to
              hear about them. Help us build a comprehensive resource for the
              research community.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 px-5 py-2 bg-slate-800 text-white text-sm font-medium rounded-lg hover:bg-slate-900 transition-colors duration-200"
            >
              Contact Us
            </a>
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer />
    </>
  );
};

export default OtherResources;

